import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  Input,
  List,
  message,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import Markdown from 'markdown-to-jsx';
import React, { useMemo, useState } from 'react';
import {
  ApiOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  SearchOutlined,
  SendOutlined,
  SyncOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { PlaygroundContext } from '../context/PlaygroundContext';
import {
  CREATE_AGENT_CONVERSATION,
  CREATE_MODEL_CONVERSATION,
  GET_ALIAS_DETAILS,
  SEND_AGENT_MESSAGE,
  SEND_MODEL_MESSAGE
} from '../graphql/mutations';
import { getUnsupportedModelMessage } from '../utils/model-validation';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea, Search } = Input;

export interface ResourceItem {
  id: string;
  name: string;
  description?: string;
  provider?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  capabilities?: string[];
  [key: string]: any; // Allow additional properties
}

export interface Message {
  role: 'user' | 'assistant' | 'agent';
  content: string;
  resolvedAlias?: string;
  resolvedVersion?: string;
}

export interface ResourceSelectorProps {
  type: 'model' | 'agent';
  title: string;
  items: ResourceItem[];
  loading: boolean;
  onItemSelect: (id: string) => void;
  selectedItemId?: string;
  partners: Array<{ label: string; value: string }>;
  selectedPartner?: string;
  onPartnerSelect: (partner: string) => void;
  showDetails?: boolean;
  // When true, and in agent flow, after selecting an agent AND an alias, the left list
  // is hidden and the details + chat expand to full width
  fullWidthAfterSelection?: boolean;
}

export const ResourceSelector: React.FC<ResourceSelectorProps> = ({
  type,
  title,
  items,
  loading,
  onItemSelect,
  selectedItemId,
  partners,
  selectedPartner,
  onPartnerSelect,
  showDetails = true,
  fullWidthAfterSelection = false
}) => {
  const { pushError } = React.useContext(PlaygroundContext);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPartnerRequired, setShowPartnerRequired] = useState(false);
  const [selectedAliasId, setSelectedAliasId] = useState<string>('');
  const [aliasDetails, setAliasDetails] = useState<any>(null);
  const [loadingAliasDetails, setLoadingAliasDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'prepared' | 'not_prepared'
  >('all');

  const prevValuesRef = React.useRef({ selectedItemId, selectedPartner });

  // Helper function to get status-based icon (matches agent definition section)
  const getStatusIcon = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
      case 'READY':
      case 'PREPARED':
        return (
          <CheckCircleOutlined
            style={{ color: '#52c41a' }}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        );
      case 'UPDATING':
      case 'CREATING':
        return (
          <SyncOutlined
            spin
            style={{ color: '#1890ff' }}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        );
      case 'FAILED':
        return (
          <ExclamationCircleOutlined
            style={{ color: '#ff4d4f' }}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        );
      default:
        return (
          <InfoCircleOutlined
            style={{ color: '#8c8c8c' }}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        );
    }
  };

  // Filter items based on search query and status
  const filteredItems = useMemo(() => {
    let filtered = items;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(query) ||
          item.id?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.provider?.toLowerCase().includes(query) ||
          item.capabilities?.some((cap) => cap.toLowerCase().includes(query))
      );
    }

    // Filter by status (only for agents)
    if (type === 'agent' && statusFilter !== 'all') {
      filtered = filtered.filter((item) => {
        const status = (item as any).status;
        if (statusFilter === 'prepared') {
          return (
            status === 'PREPARED' || status === 'ACTIVE' || status === 'READY'
          );
        } else if (statusFilter === 'not_prepared') {
          return (
            status !== 'PREPARED' && status !== 'ACTIVE' && status !== 'READY'
          );
        }
        return true;
      });
    }

    return filtered;
  }, [items, searchQuery, statusFilter, type]);

  // Conversation creation mutations
  const [createModelConversation] = useMutation(CREATE_MODEL_CONVERSATION);
  const [createAgentConversation] = useMutation(CREATE_AGENT_CONVERSATION);

  // Message sending mutations
  const [sendModelMessage, { loading: isSendingModel }] = useMutation(
    SEND_MODEL_MESSAGE,
    {
      onError: (error) => {
        // For any error when using a model, show the helpful message
        if (type === 'model' && selectedItemId) {
          const helpfulMessage: Message = {
            role: 'assistant',
            content: getUnsupportedModelMessage(selectedItemId)
          };

          // Add the helpful message to the conversation
          setMessages((prev) => [...prev, helpfulMessage]);
        } else {
          // Show generic error for agents or when no model is selected
          pushError({
            title: 'Failed to send message',
            details: error instanceof Error ? error.message : undefined
          });
        }
      }
    }
  );

  const [sendAgentMessage, { loading: isSendingAgent }] = useMutation(
    SEND_AGENT_MESSAGE,
    {
      onError: (error) => {
        pushError({
          title: 'Failed to send message',
          details: error instanceof Error ? error.message : undefined
        });
      }
    }
  );

  const [getAliasDetailsMutation] = useMutation(GET_ALIAS_DETAILS, {
    onCompleted: (data) => {
      setAliasDetails(data.getAliasDetails);
      setLoadingAliasDetails(false);
    },
    onError: (error) => {
      setLoadingAliasDetails(false);
      pushError({
        title: 'Failed to load alias details',
        details: error instanceof Error ? error.message : undefined
      });
    }
  });

  const isSending = isSendingModel || isSendingAgent;

  React.useEffect(() => {
    const prevValues = prevValuesRef.current;
    const itemIdChanged = prevValues.selectedItemId !== selectedItemId;
    const partnerChanged = prevValues.selectedPartner !== selectedPartner;

    if (itemIdChanged || partnerChanged) {
      // Clear frontend state when switching models/agents or partners
      setMessages([]);
    }

    // Reset partner and alias selection when switching models/agents (but not when partner itself changes)
    if (itemIdChanged && selectedItemId) {
      onPartnerSelect('');
      setSelectedAliasId('');
      setAliasDetails(null);
    }

    prevValuesRef.current = { selectedItemId, selectedPartner };
  }, [selectedItemId, selectedPartner]);

  // Clear partner required feedback after 3 seconds
  React.useEffect(() => {
    if (showPartnerRequired) {
      const timer = setTimeout(() => {
        setShowPartnerRequired(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showPartnerRequired]);

  const handleAliasSelect = async (aliasId: string) => {
    setSelectedAliasId(aliasId);
    setAliasDetails(null);
    setLoadingAliasDetails(true);

    // Find the selected alias to get its name
    const selectedAlias = selectedItem?.aliases?.find(
      (alias: any) => alias.id === aliasId
    );
    const aliasName = selectedAlias?.name || aliasId; // fallback to ID if name not found

    try {
      await getAliasDetailsMutation({
        variables: {
          agentName: selectedItem?.name,
          aliasName: aliasName
        }
      });
    } catch (error) {
      setLoadingAliasDetails(false);
      // Error is already handled by the mutation's onError callback
    }
  };

  const createNewConversation = async () => {
    if (!selectedItemId || !selectedPartner) {
      pushError({
        title: 'Selection required',
        description: `Please select a ${type} and partner first`
      });
      return null;
    }

    try {
      if (type === 'model') {
        const { data } = await createModelConversation({
          variables: {
            modelId: selectedItemId,
            partner: selectedPartner
          }
        });
        return data?.createModelConversation?.id;
      } else {
        const { data } = await createAgentConversation({
          variables: {
            agentName: selectedItemId,
            partner: selectedPartner
          }
        });
        return data?.createAgentConversation?.id;
      }
    } catch (error) {
      pushError({
        title: 'Failed to create conversation',
        description: 'Please try again.',
        details: error instanceof Error ? error.message : undefined
      });
      return null;
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    if (!selectedItemId || !selectedPartner) {
      pushError({
        title: 'Selection required',
        description: `Please select a ${type} and partner first`
      });
      return;
    }

    // Enforce alias selection for agents
    if (type === 'agent') {
      if (!selectedAliasId) {
        pushError({
          title: 'Alias required',
          description: 'Please select an agent alias before sending a message.'
        });
        return;
      }
      if (!aliasDetails?.agentVersion) {
        pushError({
          title: 'Alias details not loaded',
          description:
            'Please wait for alias details to load before sending a message.'
        });
        return;
      }
    }

    const newMessage: Message = {
      role: 'user',
      content: input
    };

    setMessages((prev) => [...prev, newMessage]);

    const messageToSend = input;
    setInput('');

    try {
      // Since backend is now stateless, we can use a dummy conversation ID
      // The backend uses modelId/agentName and partner directly
      const conversationId = 'stateless-' + Date.now();

      const fullConversationHistory = [...messages, newMessage].map((msg) => ({
        role: msg.role,
        content: msg.content
      }));

      if (type === 'model') {
        const { data } = await sendModelMessage({
          variables: {
            conversationId: conversationId,
            message: messageToSend,
            messages: fullConversationHistory,
            modelId: selectedItemId,
            partner: selectedPartner
          }
        });

        if (data && data.sendModelMessage) {
          setMessages((prev) => [...prev, data.sendModelMessage]);
        }
      } else {
        // Find the selected agent to get its name
        const selectedAgent = items.find((item) => item.id === selectedItemId);
        const agentName = selectedAgent?.name || selectedItemId; // fallback to ID if name not found

        // Find the selected alias to get its name
        let aliasName = undefined;
        if (selectedAliasId) {
          const selectedAlias = selectedAgent?.aliases?.find(
            (alias: any) => alias.id === selectedAliasId
          );
          aliasName = selectedAlias?.name || selectedAliasId; // fallback to ID if name not found
        }

        const { data } = await sendAgentMessage({
          variables: {
            conversationId: conversationId,
            message: messageToSend,
            messages: fullConversationHistory,
            agentName: agentName,
            partner: selectedPartner,
            aliasName: aliasName,
            aliasVersion: aliasDetails?.agentVersion
          }
        });

        if (data && data.sendAgentMessage) {
          const { role, content, resolvedAlias, resolvedVersion } =
            data.sendAgentMessage;

          setMessages((prev) => [
            ...prev,
            {
              role,
              content,
              resolvedAlias,
              resolvedVersion
            }
          ]);
        }
      }
    } catch (error) {
      pushError({
        title: 'Failed to send message',
        details: error instanceof Error ? error.message : undefined
      });
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    onPartnerSelect('');
    message.success('New conversation started');
  };

  const selectedItem = items.find((item) => item.id === selectedItemId);

  // Determine when to collapse to full-width (agent + alias selected, or model selected)
  const isAgentFullWidth =
    fullWidthAfterSelection &&
    type === 'agent' &&
    Boolean(selectedItemId) &&
    Boolean(selectedAliasId);

  const isModelFullWidth =
    fullWidthAfterSelection && type === 'model' && Boolean(selectedItemId);

  const isFullWidth = isAgentFullWidth || isModelFullWidth;

  // When requesting the Agent Interaction redesign:
  // Before alias is selected, show two bars like Agent Definitions:
  // 1) Search + Agents List, 2) Alias Selection (only after agent selected).
  const isAgentPreSelectionView =
    fullWidthAfterSelection && type === 'agent' && !selectedAliasId;

  if (isAgentPreSelectionView) {
    return (
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        {/* Bar 1: Search and Select an Agent */}
        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong style={{ marginBottom: 8, display: 'block' }}>
                Search and Select an Agent
              </Text>

              {/* Search and Filter */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <Search
                  placeholder="Search agents by name, ID, description, or capabilities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  prefix={<SearchOutlined />}
                  style={{ flex: 1 }}
                  disabled={loading}
                />
                <Segmented
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Prepared', value: 'prepared' },
                    { label: 'Not prepared', value: 'not_prepared' }
                  ]}
                  value={statusFilter}
                  onChange={(val) =>
                    setStatusFilter(val as 'all' | 'prepared' | 'not_prepared')
                  }
                  disabled={loading}
                />
              </div>

              {/* Agents List */}
              <Card
                size="small"
                bodyStyle={{ padding: 0 }}
                style={{ borderRadius: 8 }}
              >
                <Spin spinning={loading}>
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    <List
                      dataSource={filteredItems}
                      locale={{
                        emptyText: searchQuery
                          ? `No agents found matching "${searchQuery}"`
                          : 'No agents available'
                      }}
                      renderItem={(agent) => {
                        const isSelected = agent.id === selectedItemId;
                        return (
                          <List.Item
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              background: isSelected ? '#e6f7ff' : 'transparent'
                            }}
                            onClick={() => {
                              onItemSelect(agent.id);
                              setSelectedAliasId('');
                              setAliasDetails(null);
                            }}
                          >
                            <Space>
                              {getStatusIcon(agent.status)}
                              <span>{agent.name}</span>
                              {!!agent.provider && (
                                <Tag color="blue">{agent.provider}</Tag>
                              )}
                            </Space>
                          </List.Item>
                        );
                      }}
                    />
                  </div>
                </Spin>
              </Card>

              {/* Search Results Info */}
              {(searchQuery.trim() || statusFilter !== 'all') && (
                <div style={{ marginTop: 8, textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Showing {filteredItems.length} of {items.length} agents
                    {statusFilter !== 'all' &&
                      ` (${
                        statusFilter === 'not_prepared'
                          ? 'not prepared'
                          : statusFilter
                      })`}
                  </Text>
                </div>
              )}
            </div>
          </Space>
        </Card>

        {/* Bar 2: Alias Selection for selected agent */}
        {!!selectedItem && (
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Title level={5} style={{ margin: 0 }}>
                🎯 Select Agent Alias
              </Title>
              {selectedItem.aliases && selectedItem.aliases.length > 0 ? (
                <Select
                  style={{ width: '100%' }}
                  placeholder="Choose an alias to view details..."
                  value={selectedAliasId}
                  onChange={handleAliasSelect}
                  size="middle"
                  loading={loadingAliasDetails}
                >
                  {selectedItem.aliases.map((alias: any) => (
                    <Option key={alias.id} value={alias.id}>
                      <Space>
                        <span>{alias.name}</span>
                        {!!alias.status && (
                          <Tag color="blue">{alias.status}</Tag>
                        )}
                      </Space>
                    </Option>
                  ))}
                </Select>
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="This agent has no aliases"
                  description="Please contact an administrator to set up aliases for this agent."
                />
              )}
            </Space>
          </Card>
        )}
      </div>
    );
  }

  // Add utility function to extract agent prompt
  const getAgentPrompt = (config?: any) => {
    if (!config) {
      return null;
    }

    // Try different possible paths for the prompt text in AWS Bedrock agent configuration
    if (config.promptConfigurations) {
      for (const promptConfig of config.promptConfigurations) {
        if (promptConfig.basePromptTemplate) {
          return promptConfig.basePromptTemplate;
        }
        if (promptConfig.promptTemplateConfiguration?.text) {
          return promptConfig.promptTemplateConfiguration.text;
        }
        if (promptConfig.promptTemplate) {
          return promptConfig.promptTemplate;
        }
      }
    }

    // Fallback: check if the config itself contains prompt text
    if (config.basePromptTemplate) {
      return config.basePromptTemplate;
    }
    if (config.promptTemplate) {
      return config.promptTemplate;
    }
    if (config.text) {
      return config.text;
    }

    return null;
  };

  // Add utility to get inference parameters
  const getInferenceParams = (config?: any) => {
    if (!config?.promptConfigurations?.[0]?.inferenceConfiguration) {
      return null;
    }
    const params = config.promptConfigurations[0].inferenceConfiguration;
    return {
      temperature: params.temperature,
      maxTokens: params.maximumLength,
      topP: params.topP
    };
  };

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        background: 'white',
        minHeight: '80vh',
        maxWidth: '1400px',
        margin: '0 auto'
      }}
    >
      {!isFullWidth && (
        <Title level={4} style={{ marginBottom: 24, fontWeight: 500 }}>
          {title}
        </Title>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#8c8c8c' }}>
            Loading {type === 'model' ? 'models' : 'agents'} from AWS Bedrock...
          </div>
        </div>
      ) : (
        <>
          <Row gutter={[24, 24]}>
            {/* Left Column - Resource Selection */}
            {!isFullWidth && (
              <Col xs={24} lg={8}>
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: '100%' }}
                >
                  {/* Resource Search and List */}
                  <div>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>
                      Available {type === 'model' ? 'Models' : 'Agents'}
                    </Text>

                    {/* Search and Filter */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                      <Search
                        placeholder={`Search ${
                          type === 'model' ? 'models' : 'agents'
                        }...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        allowClear
                        prefix={React.createElement(
                          SearchOutlined as unknown as React.FC
                        )}
                        style={{ flex: 1 }}
                      />
                      {type === 'agent' && (
                        <Segmented
                          options={[
                            { label: 'All', value: 'all' },
                            { label: 'Prepared', value: 'prepared' },
                            { label: 'Not prepared', value: 'not_prepared' }
                          ]}
                          value={statusFilter}
                          onChange={(val) =>
                            setStatusFilter(
                              val as 'all' | 'prepared' | 'not_prepared'
                            )
                          }
                        />
                      )}
                    </div>

                    {filteredItems.length === 0 ? (
                      <Empty
                        description={
                          searchQuery.trim()
                            ? `No ${
                                type === 'model' ? 'models' : 'agents'
                              } found matching "${searchQuery}"`
                            : `No ${
                                type === 'model' ? 'models' : 'agents'
                              } available`
                        }
                        style={{ padding: '40px 0' }}
                      />
                    ) : (
                      <List
                        dataSource={filteredItems}
                        renderItem={(item) => (
                          <List.Item style={{ padding: 0, marginBottom: 8 }}>
                            <Card
                              hoverable
                              size="small"
                              style={{
                                width: '100%',
                                cursor: 'pointer',
                                border:
                                  selectedItemId === item.id
                                    ? '2px solid #1890ff'
                                    : '1px solid #f0f0f0',
                                borderRadius: 8,
                                transition: 'all 0.3s ease'
                              }}
                              onClick={() => onItemSelect(item.id)}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12
                                }}
                              >
                                <Avatar
                                  icon={
                                    type === 'model'
                                      ? React.createElement(
                                          CloudOutlined as unknown as React.FC
                                        )
                                      : getStatusIcon((item as any).status)
                                  }
                                  style={{
                                    backgroundColor:
                                      selectedItemId === item.id
                                        ? '#1890ff'
                                        : '#f5f5f5',
                                    color:
                                      selectedItemId === item.id
                                        ? 'white'
                                        : '#8c8c8c'
                                  }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      fontSize: 14,
                                      color:
                                        selectedItemId === item.id
                                          ? '#1890ff'
                                          : '#262626',
                                      marginBottom: 4,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {item.name}
                                  </div>
                                  {!!item.provider && (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                      }}
                                    >
                                      <Tag
                                        style={{
                                          margin: 0,
                                          fontSize: 11,
                                          padding: '2px 6px',
                                          borderRadius: 4
                                        }}
                                      >
                                        {item.provider}
                                      </Tag>
                                      {!!item.status && (
                                        <Tag
                                          color={
                                            item.status === 'ACTIVE' ||
                                            item.status === 'READY'
                                              ? 'green'
                                              : item.status === 'UPDATING' ||
                                                item.status === 'CREATING'
                                              ? 'blue'
                                              : 'red'
                                          }
                                          style={{
                                            margin: 0,
                                            fontSize: 11,
                                            padding: '2px 6px',
                                            borderRadius: 4
                                          }}
                                        >
                                          {item.status}
                                        </Tag>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Card>
                          </List.Item>
                        )}
                        style={{ maxHeight: 400, overflowY: 'auto' }}
                      />
                    )}

                    {/* Search Results Info */}
                    {(searchQuery.trim() ||
                      (type === 'agent' && statusFilter !== 'all')) && (
                      <div style={{ marginTop: 8, textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Showing {filteredItems.length} of {items.length}{' '}
                          {type === 'model' ? 'models' : 'agents'}
                          {type === 'agent' &&
                            statusFilter !== 'all' &&
                            ` (${
                              statusFilter === 'not_prepared'
                                ? 'not prepared'
                                : statusFilter
                            })`}
                        </Text>
                      </div>
                    )}
                  </div>
                </Space>
              </Col>
            )}

            {/* Right Column - Details and Chat */}
            <Col xs={24} lg={isFullWidth ? 24 : 16}>
              <Space
                direction="vertical"
                size="large"
                style={{ width: '100%' }}
              >
                {/* Selected Item Details - show always above conversation (agent + model) */}
                {!!showDetails && !!selectedItem && (
                  <Card
                    title={
                      <Space>
                        {type === 'model'
                          ? React.createElement(
                              CloudOutlined as unknown as React.FC
                            )
                          : getStatusIcon(selectedItem.status)}
                        <span>{selectedItem.name}</span>
                      </Space>
                    }
                    size="small"
                    style={{ borderRadius: 12 }}
                    bodyStyle={{ padding: '12px 16px' }}
                  >
                    <Space
                      direction="vertical"
                      size={10}
                      style={{ width: '100%' }}
                    >
                      {/* Compact Key Information */}
                      <div>
                        <Space
                          direction="vertical"
                          size={6}
                          style={{ width: '100%' }}
                        >
                          {/* Agent/Model ID, Status, and Updated Date with equal spacing */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flex: 1
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{ fontSize: 12, fontWeight: 500 }}
                              >
                                {type === 'model' ? 'Model ID:' : 'Agent ID:'}
                              </Text>
                              <Text
                                code
                                style={{
                                  fontSize: 11,
                                  padding: '2px 6px',
                                  borderRadius: 4
                                }}
                              >
                                {selectedItem.id}
                              </Text>
                            </div>

                            {/* Status */}
                            {!!selectedItem.status && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  flex: 1,
                                  justifyContent: 'center'
                                }}
                              >
                                <Text
                                  type="secondary"
                                  style={{ fontSize: 12, fontWeight: 500 }}
                                >
                                  Status:
                                </Text>
                                <Tag
                                  color={
                                    selectedItem.status === 'ACTIVE' ||
                                    selectedItem.status === 'READY' ||
                                    selectedItem.status === 'PREPARED'
                                      ? 'green'
                                      : selectedItem.status === 'UPDATING' ||
                                        selectedItem.status === 'CREATING'
                                      ? 'blue'
                                      : 'red'
                                  }
                                  style={{ margin: 0, borderRadius: 6 }}
                                >
                                  {selectedItem.status}
                                </Tag>
                              </div>
                            )}

                            {/* Updated Date (only for agents when no alias is selected) */}
                            {type === 'agent' &&
                              !!selectedItem.updatedAt &&
                              !selectedAliasId && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flex: 1,
                                    justifyContent: 'flex-end'
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12, fontWeight: 500 }}
                                  >
                                    Updated:
                                  </Text>
                                  <Text style={{ fontSize: 12 }}>
                                    {new Date(
                                      selectedItem.updatedAt
                                    ).toLocaleDateString()}
                                  </Text>
                                </div>
                              )}

                            {/* Alias Dates (only when alias is selected and details are available) */}
                            {type === 'agent' &&
                              !!selectedAliasId &&
                              !!aliasDetails &&
                              !!(
                                aliasDetails.createdAt || aliasDetails.updatedAt
                              ) && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    flex: 1,
                                    justifyContent: 'flex-end',
                                    flexWrap: 'nowrap'
                                  }}
                                >
                                  {!!aliasDetails.createdAt && (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 500
                                        }}
                                      >
                                        Created:
                                      </Text>
                                      <Text style={{ fontSize: 12 }}>
                                        {new Date(
                                          aliasDetails.createdAt
                                        ).toLocaleString(undefined, {
                                          year: '2-digit',
                                          month: 'numeric',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit'
                                        })}
                                      </Text>
                                    </div>
                                  )}
                                  {!!aliasDetails.updatedAt && (
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: 12,
                                          fontWeight: 500
                                        }}
                                      >
                                        Updated:
                                      </Text>
                                      <Text style={{ fontSize: 12 }}>
                                        {new Date(
                                          aliasDetails.updatedAt
                                        ).toLocaleString(undefined, {
                                          year: '2-digit',
                                          month: 'numeric',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit'
                                        })}
                                      </Text>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>

                          {/* For models: Provider and Streaming Support on same line */}
                          {type === 'model' && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}
                            >
                              {/* Provider */}
                              {!!selectedItem.provider && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flex: 1
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12, fontWeight: 500 }}
                                  >
                                    Provider:
                                  </Text>
                                  <Tag style={{ margin: 0, borderRadius: 6 }}>
                                    {selectedItem.provider}
                                  </Tag>
                                </div>
                              )}

                              {/* Streaming Support */}
                              {selectedItem.responseStreamingSupported !==
                                undefined && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flex: 1,
                                    justifyContent: 'flex-end'
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12, fontWeight: 500 }}
                                  >
                                    Streaming Support:
                                  </Text>
                                  {selectedItem.responseStreamingSupported ? (
                                    <Tag
                                      color="green"
                                      style={{ margin: 0, borderRadius: 6 }}
                                    >
                                      Supported
                                    </Tag>
                                  ) : (
                                    <Tag
                                      color="default"
                                      style={{ margin: 0, borderRadius: 6 }}
                                    >
                                      Not Supported
                                    </Tag>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Provider for agents only */}
                          {type === 'agent' && !!selectedItem.provider && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  minWidth: 80
                                }}
                              >
                                Provider:
                              </Text>
                              <Tag style={{ margin: 0, borderRadius: 6 }}>
                                {selectedItem.provider}
                              </Tag>
                            </div>
                          )}
                        </Space>
                      </div>

                      {/* Description - Only show for agents */}
                      {type === 'agent' && (
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8
                            }}
                          >
                            <Text
                              type="secondary"
                              style={{
                                fontSize: 12,
                                fontWeight: 500,
                                minWidth: 80
                              }}
                            >
                              Description:
                            </Text>
                            <Text
                              style={{
                                fontSize: 13,
                                lineHeight: 1.4,
                                flex: 1,
                                color: selectedItem.description
                                  ? 'inherit'
                                  : '#bfbfbf'
                              }}
                            >
                              {selectedItem.description ||
                                'No description available'}
                            </Text>
                          </div>
                        </div>
                      )}

                      {/* Agent-specific details - Enhanced for better interaction guidance */}
                      {type === 'agent' && (
                        <Space
                          direction="vertical"
                          size={12}
                          style={{ width: '100%' }}
                        >
                          {/* Agent Alias Selection */}
                          {!!selectedItem &&
                            !!selectedItem.aliases &&
                            selectedItem.aliases.length > 0 && (
                              <div>
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    display: 'block',
                                    marginBottom: 6,
                                    color: '#1890ff'
                                  }}
                                >
                                  🎯 Select Agent Alias:
                                </Text>
                                <Select
                                  style={{ width: '100%' }}
                                  placeholder="Choose an alias to view details..."
                                  value={selectedAliasId}
                                  onChange={handleAliasSelect}
                                  size="small"
                                  loading={loadingAliasDetails}
                                >
                                  {selectedItem.aliases.map((alias: any) => (
                                    <Option key={alias.id} value={alias.id}>
                                      <Space>
                                        <span>{alias.name}</span>
                                        {!!alias.status && (
                                          <Tag color="blue">{alias.status}</Tag>
                                        )}
                                      </Space>
                                    </Option>
                                  ))}
                                </Select>
                              </div>
                            )}

                          {/* No Aliases Warning Message */}
                          {!!selectedItem &&
                            (!selectedItem.aliases ||
                              selectedItem.aliases.length === 0) && (
                              <Alert
                                type="error"
                                showIcon
                                message="Cannot interact with this agent as it has no aliases!"
                                description="Please contact an administrator to set up aliases for this agent."
                                style={{ marginBottom: 12 }}
                              />
                            )}

                          {/* Agent Instructions/Prompt - Most Important for Interaction - Only show if alias selected */}
                          {!!selectedAliasId && !!aliasDetails && (
                            <div>
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  display: 'block',
                                  marginBottom: 6,
                                  color: '#1890ff'
                                }}
                              >
                                🤖 Agent Instructions & Expected Inputs:
                              </Text>
                              {aliasDetails.agentInstructions ? (
                                <Card
                                  size="small"
                                  style={{
                                    marginBottom: 8,
                                    backgroundColor: '#f0f8ff',
                                    border: '1px solid #e6f4ff'
                                  }}
                                >
                                  <div
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      fontFamily: 'monospace',
                                      fontSize: 11,
                                      lineHeight: 1.4,
                                      maxHeight: 200,
                                      overflow: 'auto'
                                    }}
                                  >
                                    {aliasDetails.agentInstructions}
                                  </div>
                                </Card>
                              ) : (
                                <Alert
                                  message="No instructions available for this alias"
                                  type="info"
                                />
                              )}
                            </div>
                          )}

                          {/* Loading state for alias details */}
                          {!!loadingAliasDetails && (
                            <Card size="small" style={{ textAlign: 'center' }}>
                              <Spin size="small" />
                              <Text style={{ marginLeft: 8, fontSize: 12 }}>
                                Loading alias details...
                              </Text>
                            </Card>
                          )}

                          {/* Original Agent Instructions - Hidden when alias functionality is active */}
                          {(!selectedItem.aliases ||
                            selectedItem.aliases.length === 0) && (
                            <div>
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  display: 'block',
                                  marginBottom: 6,
                                  color: '#1890ff'
                                }}
                              >
                                🤖 Agent Instructions & Expected Inputs:
                              </Text>
                              {selectedItem.agentInstructions ? (
                                <Card
                                  size="small"
                                  style={{
                                    marginBottom: 8,
                                    backgroundColor:
                                      selectedItem.agentInstructions.includes(
                                        'default AWS Bedrock system prompt'
                                      )
                                        ? '#fff7e6'
                                        : '#f0f8ff',
                                    border:
                                      selectedItem.agentInstructions.includes(
                                        'default AWS Bedrock system prompt'
                                      )
                                        ? '1px solid #ffd591'
                                        : '1px solid #e6f4ff'
                                  }}
                                >
                                  <div
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      fontFamily:
                                        selectedItem.agentInstructions.includes(
                                          'default AWS Bedrock system prompt'
                                        )
                                          ? 'inherit'
                                          : 'monospace',
                                      fontSize: '12px',
                                      lineHeight: '1.4',
                                      maxHeight: '120px',
                                      overflowY: 'auto',
                                      fontStyle:
                                        selectedItem.agentInstructions.includes(
                                          'default AWS Bedrock system prompt'
                                        )
                                          ? 'italic'
                                          : 'normal'
                                    }}
                                  >
                                    {selectedItem.agentInstructions}
                                  </div>
                                </Card>
                              ) : (
                                <div>
                                  <Text
                                    type="secondary"
                                    style={{
                                      fontSize: 12,
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    No specific instructions configured for this
                                    agent
                                  </Text>
                                </div>
                              )}

                              {/* Inference Parameters from version details */}
                              {!!selectedItem.versionDetails
                                ?.promptOverrideConfiguration &&
                                (() => {
                                  const inferenceParams = getInferenceParams(
                                    selectedItem.versionDetails
                                      .promptOverrideConfiguration
                                  );

                                  return inferenceParams ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 8,
                                        flexWrap: 'wrap'
                                      }}
                                    >
                                      {inferenceParams.temperature !==
                                        undefined && (
                                        <Tag
                                          style={{ fontSize: 10, margin: 0 }}
                                        >
                                          Temp: {inferenceParams.temperature}
                                        </Tag>
                                      )}
                                      {inferenceParams.maxTokens !==
                                        undefined && (
                                        <Tag
                                          style={{ fontSize: 10, margin: 0 }}
                                        >
                                          Max: {inferenceParams.maxTokens}
                                        </Tag>
                                      )}
                                      {inferenceParams.topP !== undefined && (
                                        <Tag
                                          style={{ fontSize: 10, margin: 0 }}
                                        >
                                          TopP: {inferenceParams.topP}
                                        </Tag>
                                      )}
                                    </div>
                                  ) : null;
                                })()}
                            </div>
                          )}

                          {/* Foundation Model - Show from alias details when alias selected */}
                          {!!selectedAliasId &&
                            !!aliasDetails &&
                            !!aliasDetails.foundationModel && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8
                                }}
                              >
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    minWidth: 120
                                  }}
                                >
                                  Foundation Model:
                                </Text>
                                <Tag
                                  color="purple"
                                  style={{ margin: 0, borderRadius: 6 }}
                                >
                                  {aliasDetails.foundationModel}
                                </Tag>
                              </div>
                            )}

                          {/* Show original foundation model only when no aliases available */}
                          {(!selectedItem.aliases ||
                            selectedItem.aliases.length === 0) &&
                            !!selectedItem.foundationModel && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8
                                }}
                              >
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    minWidth: 120
                                  }}
                                >
                                  Foundation Model:
                                </Text>
                                <Tag
                                  color="purple"
                                  style={{ margin: 0, borderRadius: 6 }}
                                >
                                  {selectedItem.foundationModel}
                                </Tag>
                              </div>
                            )}

                          {/* Agent Version */}
                          {!!selectedItem.agentVersion && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  minWidth: 120
                                }}
                              >
                                Version:
                              </Text>
                              <Tag
                                color="orange"
                                style={{ margin: 0, borderRadius: 6 }}
                              >
                                v{selectedItem.agentVersion}
                              </Tag>
                            </div>
                          )}
                        </Space>
                      )}

                      {/* Model-specific details */}
                      {type === 'model' && (
                        <Space
                          direction="vertical"
                          size={6}
                          style={{ width: '100%' }}
                        >
                          {/* Inference Type */}
                          {!!selectedItem.inferenceType && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  minWidth: 120
                                }}
                              >
                                Inference Type:
                              </Text>
                              <Tag
                                color="cyan"
                                style={{ margin: 0, borderRadius: 6 }}
                              >
                                {selectedItem.inferenceType}
                              </Tag>
                            </div>
                          )}

                          {/* Model ARN (truncated) */}
                          {!!selectedItem.modelArn && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  minWidth: 120
                                }}
                              >
                                Model ARN:
                              </Text>
                              <Text
                                code
                                style={{
                                  fontSize: 11,
                                  padding: '2px 6px',
                                  borderRadius: 4
                                }}
                              >
                                ...{selectedItem.modelArn.slice(-20)}
                              </Text>
                            </div>
                          )}
                        </Space>
                      )}

                      {/* Capabilities */}
                      {!!selectedItem.capabilities &&
                        selectedItem.capabilities.length > 0 && (
                          <div>
                            <Text
                              type="secondary"
                              style={{ fontSize: 12, fontWeight: 500 }}
                            >
                              Capabilities:
                            </Text>
                            <div style={{ marginTop: 4 }}>
                              {selectedItem.capabilities.map(
                                (capability, index) => (
                                  <Tag
                                    key={index}
                                    style={{
                                      margin: '2px 4px 2px 0',
                                      borderRadius: 6
                                    }}
                                  >
                                    {capability}
                                  </Tag>
                                )
                              )}
                            </div>
                          </div>
                        )}
                    </Space>
                  </Card>
                )}

                {/* Chat Interface - Only show:
                    - For model flow: when a model is selected (unchanged behavior)
                    - For agent flow: only after an alias was selected, and only if aliases exist */}
                {!!(type !== 'agent'
                  ? true
                  : Boolean(
                      selectedItem &&
                        selectedItem.aliases &&
                        selectedItem.aliases.length > 0 &&
                        selectedAliasId
                    )) && (
                  <>
                    <Card
                      style={{
                        minHeight: '500px',
                        borderRadius: 8,
                        background: '#fafafa',
                        flex: 1
                      }}
                      title={
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              {React.createElement(
                                SendOutlined as unknown as React.FC
                              )}
                              <span>Conversation</span>
                            </div>
                            <Button
                              icon={React.createElement(
                                PlusOutlined as unknown as React.FC
                              )}
                              onClick={handleNewConversation}
                              disabled={!selectedItemId}
                              size="small"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                borderRadius: 6
                              }}
                            >
                              New Conversation
                            </Button>
                          </div>
                          <Button
                            icon={<PlusOutlined />}
                            onClick={handleNewConversation}
                            disabled={
                              !selectedItemId ||
                              (type === 'agent' && !selectedAliasId)
                            }
                            size="small"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              borderRadius: 6
                            }}
                          >
                            New Conversation
                          </Button>
                        </div>
                      }
                    >
                      <div
                        style={{
                          height: '400px',
                          overflowY: 'auto',
                          marginBottom: '16px'
                        }}
                      >
                        <div
                          style={{
                            height: '400px',
                            overflowY: 'auto',
                            marginBottom: '16px'
                          }}
                        >
                          <List
                            dataSource={messages}
                            locale={{
                              emptyText: selectedItemId
                                ? `Start a conversation with the selected ${type}`
                                : `Select a ${type} to begin`
                            }}
                            renderItem={(message) => (
                              <List.Item>
                                <Card
                                  style={{
                                    width: '100%',
                                    backgroundColor:
                                      message.role === 'user'
                                        ? '#f0f0f0'
                                        : '#e6f7ff',
                                    borderRadius: 8,
                                    marginBottom: 8
                                  }}
                                >
                                  <Space direction="vertical">
                                    <div style={{ position: 'relative' }}>
                                      <Text strong>
                                        {message.role === 'user'
                                          ? 'You'
                                          : message.role === 'agent'
                                          ? 'Agent'
                                          : 'Assistant'}
                                      </Text>
                                      <div
                                        style={{
                                          lineHeight: '1.6',
                                          fontSize: '14px',
                                          marginTop: '4px',
                                          marginBottom: '12px'
                                        }}
                                      >
                                        {message.role === 'agent' &&
                                          !!message.resolvedAlias && (
                                            <Tooltip
                                              title={
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '2px'
                                                  }}
                                                >
                                                  <div>
                                                    alias:{' '}
                                                    {message.resolvedAlias}
                                                  </div>
                                                  <div>
                                                    version:{' '}
                                                    {message.resolvedVersion ||
                                                      'unknown'}
                                                  </div>
                                                </div>
                                              }
                                            >
                                              {React.createElement(
                                                QuestionCircleOutlined as unknown as React.FC,
                                                {
                                                  style: {
                                                    position: 'absolute',
                                                    bottom: -20,
                                                    right: 0,
                                                    fontSize: '14px',
                                                    color: '#8c8c8c',
                                                    cursor: 'help'
                                                  }
                                                }
                                              )}
                                            </Tooltip>
                                          )}
                                      </div>
                                      <Markdown
                                        options={{
                                          overrides: {
                                            code: {
                                              props: {
                                                style: {
                                                  backgroundColor: '#f5f5f5',
                                                  padding: '2px 4px',
                                                  borderRadius: '3px',
                                                  fontFamily: 'monospace',
                                                  fontSize: '13px'
                                                }
                                              }
                                            },
                                            pre: {
                                              props: {
                                                style: {
                                                  backgroundColor: '#f5f5f5',
                                                  padding: '12px',
                                                  borderRadius: '6px',
                                                  overflow: 'auto',
                                                  fontFamily: 'monospace',
                                                  fontSize: '13px',
                                                  border: '1px solid #e8e8e8'
                                                }
                                              }
                                            },
                                            blockquote: {
                                              props: {
                                                style: {
                                                  borderLeft:
                                                    '4px solid #d9d9d9',
                                                  paddingLeft: '12px',
                                                  margin: '8px 0',
                                                  fontStyle: 'italic',
                                                  color: '#666'
                                                }
                                              }
                                            },
                                            ul: {
                                              props: {
                                                style: {
                                                  paddingLeft: '20px',
                                                  margin: '8px 0'
                                                }
                                              }
                                            },
                                            ol: {
                                              props: {
                                                style: {
                                                  paddingLeft: '20px',
                                                  margin: '8px 0'
                                                }
                                              }
                                            },
                                            h1: {
                                              props: {
                                                style: {
                                                  fontSize: '18px',
                                                  fontWeight: 'bold',
                                                  margin: '12px 0 8px 0'
                                                }
                                              }
                                            },
                                            h2: {
                                              props: {
                                                style: {
                                                  fontSize: '16px',
                                                  fontWeight: 'bold',
                                                  margin: '10px 0 6px 0'
                                                }
                                              }
                                            },
                                            h3: {
                                              props: {
                                                style: {
                                                  fontSize: '15px',
                                                  fontWeight: 'bold',
                                                  margin: '8px 0 4px 0'
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      >
                                        {message.content}
                                      </Markdown>
                                    </div>
                                  </Space>
                                </Card>
                              </List.Item>
                            )}
                          />
                        </div>
                      </div>
                    </Card>

                    {/* Partner Selection */}
                    <div style={{ marginBottom: 16 }}>
                      <Text
                        strong
                        style={{ marginBottom: 8, display: 'block' }}
                      >
                        Select Partner
                        <Tooltip
                          title="Partner context for the conversation. Each conversation must be scoped to a single partner to maintain data isolation and compliance."
                          placement="right"
                        >
                          <InfoCircleOutlined
                            style={{ marginLeft: 8, color: '#8c8c8c' }}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          />
                        </Tooltip>
                      </Text>
                      <Select
                        style={{
                          width: '100%',
                          ...(showPartnerRequired && {
                            borderColor: '#ff4d4f',
                            boxShadow: '0 0 0 2px rgba(255, 77, 79, 0.2)'
                          })
                        }}
                        placeholder="Select a partner"
                        value={selectedPartner || undefined}
                        onChange={onPartnerSelect}
                        size="large"
                      >
                        {partners.map((partner) => (
                          <Option key={partner.value} value={partner.value}>
                            <Space>
                              <TeamOutlined
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                              />
                              {partner.label}
                            </Space>
                          </Option>
                        ))}
                      </Select>
                    </div>

                    {/* Message Input */}
                    <div style={{ position: 'relative' }}>
                      <Card style={{ borderRadius: 8 }}>
                        <Space.Compact style={{ width: '100%' }}>
                          <TextArea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={`Type your message to the ${type}...`}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            onPressEnter={(e) => {
                              if (!e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                              }
                            }}
                            disabled={
                              !selectedItemId ||
                              !selectedPartner ||
                              (type === 'agent' &&
                                (!selectedAliasId ||
                                  !aliasDetails?.agentVersion))
                            }
                            style={{ resize: 'none' }}
                          />
                          <Button
                            type="primary"
                            icon={
                              <SendOutlined
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                              />
                            }
                            onClick={handleSend}
                            loading={isSending}
                            disabled={
                              !selectedItemId ||
                              !selectedPartner ||
                              !input.trim() ||
                              (type === 'agent' &&
                                (!selectedAliasId ||
                                  !aliasDetails?.agentVersion))
                            }
                            style={{
                              height: 'auto',
                              alignSelf: 'flex-end',
                              borderRadius: '0 6px 6px 0'
                            }}
                          >
                            Send
                          </Button>
                        </Space.Compact>
                      </Card>
                      {!selectedPartner && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            backdropFilter: 'blur(1px)',
                            cursor: 'pointer'
                          }}
                          onClick={() => setShowPartnerRequired(true)}
                        >
                          <Text
                            style={{
                              color: '#8c8c8c',
                              fontSize: 16,
                              fontWeight: 500
                            }}
                          >
                            Select a partner to start messaging
                          </Text>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
};
